import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { api } from "../utils/Api";
import {
	Typography,
	Flex,
	Button,
	notification,
	Row,
	Col,
	Tag,
	DatePicker,
	Input,
	Select,
	Modal,
} from "antd";
import Layout from "../layout/Layout";
import { CustomSpinner, CustomTable } from "../utils/CustomComponents";
import { EditFilled, CommentOutlined } from "@ant-design/icons";
import ClaimInfoTop from "./ClaimInfoTop";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import ClaimComment from "./ClaimComment";
import ClaimType from "./ClaimType";
dayjs.extend(duration);

const { RangePicker } = DatePicker;
const { Search } = Input;
const { Text } = Typography;

const statusOptions = [
	{
		value: "All",
		label: "All",
	},
	{
		value: "Open",
		label: "Open",
	},
	{
		value: "In Progress",
		label: "In Progress",
	},
	{
		value: "Closed",
		label: "Closed",
	},
];

const optionsClaimArea = [
	{
		value: "All",
		label: "All",
	},
	{
		value: "QC",
		label: "QC",
	},
	{
		value: "Logistics",
		label: "Logistics",
	},
	{
		value: "Production",
		label: "Production",
	},
	{
		value: "Operations",
		label: "Operations",
	},
	{
		value: "Sales",
		label: "Sales",
	},
	{
		value: "A/R",
		label: "A/R",
	},
];

const ClaimDashboard = ({ history }) => {
	const [loading, setLoading] = useState(false);
	const [claimList, setClaimList] = useState([]);
	const [apiNotification, contextHolder] = notification.useNotification();
	const [startDate, setStartDate] = useState(dayjs());
	const [endDate, setEndDate] = useState(dayjs());
	const [term, setTerm] = useState("");
	const [changed, setChanged] = useState(false);
	const [status, setStatus] = useState("All");
	const [area, setArea] = useState("All");
	const [modalCommentsOpen, setModalCommentsOpen] = useState(false);
	const [modalTypeOpen, setModalTypeOpen] = useState(false);
	const currentClaimId = useRef(null);

	const openNotification = (type, message, description) => {
		apiNotification[type]({
			message: message,
			description: description,
		});
	};

	/**
	 ** Options header (columns)
	 */
	const columns = [
		{
			title: "Number",
			dataIndex: "number",
			key: "number",
			render: (value) => {
				return <Text strong>{value}</Text>;
			},
		},
		{
			title: "Date",
			dataIndex: "date",
			key: "date",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Claim's Owner",
			dataIndex: "owner",
			key: "onwer",
			render: (value) => {
				return <Text>{value.name}</Text>;
			},
		},
		{
			title: "Area",
			dataIndex: "area",
			key: "area",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Customer's Name",
			dataIndex: "customer",
			key: "customer",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Description",
			dataIndex: "description",
			key: "description",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Users Involved",
			dataIndex: "usersInvolved",
			key: "usersInvolved",
			render: (value) => {
				return (
					<div>
						{value.map((item, index) => (
							<p key={index}>- {item.name}</p>
						))}
					</div>
				);
			},
		},
		{
			title: "Level",
			dataIndex: "level",
			key: "level",
			render: (value) => {
				let color = "default";
				let hours = "(0 Hrs)";
				switch (value) {
					case "Low":
						color = "yellow";
						hours = "(3 Days)";
						break;

					case "Medium":
						color = "green";
						hours = "(1-2 Days)";
						break;

					case "High":
						color = "red";
						hours = "(24 Hrs)";
						break;

					default:
						break;
				}
				return <Tag color={color}>{`${value} ${hours}`}</Tag>;
			},
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			render: (value) => {
				let color = "default";
				switch (value) {
					case "Open":
						color = "blue";
						break;

					case "In Progress":
						color = "green";
						break;

					case "Closed":
						color = "red";
						break;

					default:
						break;
				}
				return <Tag color={color}>{value}</Tag>;
			},
		},
		{
			title: "Open Time",
			dataIndex: "openTime",
			key: "openTime",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Closed Date",
			dataIndex: "closedDate",
			key: "closedDate",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "",
			dataIndex: "edit",
			width: 100,
			render: (_, record) => {
				let enabledUsers = [
					"ec@57concrete.com",
					"pc@57concrete.com",
					"jc@57concrete.com",
					"gg@57xgroup.com",
					"eliud@gmail.com",
					"bp@57concrete.com",
					"bpa@57concrete.com",
					"mgr@57concrete.com",
				];
				record.usersInvolved.map((item) => {
					enabledUsers.push(item.email);
				});
				enabledUsers.push(record.owner.email);

				const isEnabled = enabledUsers.includes(localStorage.getItem("email"));
				return (
					<Row gutter={16}>
						{isEnabled && (
							<Button
								type="primary"
								icon={<EditFilled twoToneColor={"white"} />}
								onClick={(e) => history.push(`/claim/update/${record.id}`)}
							/>
						)}
						&nbsp;
						{isEnabled && (
							<Button
								type="primary"
								icon={<CommentOutlined twoToneColor={"white"} />}
								onClick={() => {
									currentClaimId.current = record.id;
									setModalCommentsOpen(true);
								}}
							/>
						)}
					</Row>
				);
			},
		},
	];

	useEffect(() => {
		getLoanListOpen();
	}, []);

	const getLoanListOpen = async () => {
		setLoading(true);
		setChanged((prev) => !prev);

		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };
		const url = api + `/claim/list/open/57Concrete`;

		try {
			const { data } = await axios.get(url);
			let list = [];

			if (data.success) {
				data.payload.map((item, index) => {
					const createdAt = dayjs(new Date(item.createdAt));
					const currentDate = item.closedDate
						? dayjs(new Date(item.closedDate))
						: dayjs();

					const diffInMs = currentDate.diff(createdAt);

					const dur = dayjs.duration(diffInMs);

					const days = Math.floor(dur.asDays());
					const hours = dur.hours();

					list.push({
						key: index,
						id: item._id,
						number: item.number,
						date: Intl.DateTimeFormat("en-US", {
							month: "numeric",
							year: "numeric",
							day: "numeric",
							hour: "2-digit",
							minute: "2-digit",
							second: "2-digit",
						}).format(new Date(item.createdAt)),
						closedDate: item.closedDate
							? Intl.DateTimeFormat("en-US", {
									month: "numeric",
									year: "numeric",
									day: "numeric",
									hour: "2-digit",
									minute: "2-digit",
									second: "2-digit",
								}).format(new Date(item.closedDate))
							: "NA",
						owner: item.owner,
						area: item.area,
						customer: item.customer.name,
						description: item.description,
						usersInvolved: item.usersInvolved,
						level: item.level,
						status: item.status,
						openTime: `${days} days and ${hours} hours`,
					});
				});
				setClaimList(list);
			} else {
				openNotification("error", "Error", data.message);
			}

			setLoading(false);
		} catch (err) {
			console.error(err.message);
			openNotification("error", "Error", err.message);
			return null;
		}
	};

	const getLoanList = async (term) => {
		setLoading(true);
		setTerm(term);
		setChanged((prev) => !prev);
		const start = `${startDate.year()}-${
			startDate.month() + 1
		}-${startDate.date()}`;
		const end = `${endDate.year()}-${endDate.month() + 1}-${endDate.date()}`;

		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };
		const url =
			api +
			`/claim/list?startDate=${start}&endDate=${end}&term=${term}&area=${area}&status=${status}`;

		try {
			const { data } = await axios.get(url);
			let list = [];

			if (data.success) {
				data.payload.map((item, index) => {
					const createdAt = dayjs(new Date(item.createdAt));
					const currentDate = item.closedDate
						? dayjs(new Date(item.closedDate))
						: dayjs();

					const diffInMs = currentDate.diff(createdAt);

					const dur = dayjs.duration(diffInMs);

					const days = Math.floor(dur.asDays());
					const hours = dur.hours();

					list.push({
						key: index,
						id: item._id,
						number: item.number,
						date: Intl.DateTimeFormat("en-US", {
							month: "numeric",
							year: "numeric",
							day: "numeric",
							hour: "2-digit",
							minute: "2-digit",
							second: "2-digit",
						}).format(new Date(item.createdAt)),
						closedDate: item.closedDate
							? Intl.DateTimeFormat("en-US", {
									month: "numeric",
									year: "numeric",
									day: "numeric",
									hour: "2-digit",
									minute: "2-digit",
									second: "2-digit",
								}).format(new Date(item.closedDate))
							: "NA",
						owner: item.owner,
						area: item.area,
						customer: item.customer.name,
						description: item.description,
						usersInvolved: item.usersInvolved,
						level: item.level,
						status: item.status,
						openTime: `${days} days and ${hours} hours`,
					});
				});
				setClaimList(list);
			} else {
				openNotification("error", "Error", data.message);
			}

			setLoading(false);
		} catch (err) {
			console.error(err.message);
			openNotification("error", "Error", err.message);
			return null;
		}
	};

	const onRangeChange = (dates) => {
		if (dates) {
			setStartDate(dates[0]);
			setEndDate(dates[1]);
		}
	};

	const RenderTable = () => {
		return (
			<Flex gap={"middle"} vertical>
				<Row gap={[16, 16]}>
					<Col>
						<Button
							type="primary"
							onClick={() => setModalTypeOpen(true)}
							size="large"
						>
							Add new claim
						</Button>
					</Col>
				</Row>

				<CustomTable columns={columns} data={claimList} isScroll={true} />
			</Flex>
		);
	};

	const onSearch = (value, _e, info) => {
		if (info?.source === "input") {
			getLoanList(value);
		}
	};

	const filterOption = (input, option) =>
		(option?.label ?? "").toLowerCase().includes(input.toLowerCase());

	const RenderSearch = () => {
		return (
			<Flex gap={10}>
				<RangePicker
					defaultValue={[
						dayjs(startDate, "MM/DD/YYYY"),
						dayjs(endDate, "MM/DD/YYYY"),
					]}
					onChange={onRangeChange}
					format={"MM/DD/YYYY"}
					size="large"
					disabled={loading}
					allowClear={false}
				/>
				<Select
					showSearch
					placeholder={`Select status`}
					optionFilterProp="children"
					size="large"
					onChange={(value) => setStatus(value)}
					filterOption={filterOption}
					options={statusOptions}
					disabled={loading}
					defaultValue={"All"}
					value={status}
					style={{
						width: "200px",
					}}
				/>
				<Select
					showSearch
					placeholder={`Select area`}
					optionFilterProp="children"
					size="large"
					onChange={(value) => setArea(value)}
					filterOption={filterOption}
					options={optionsClaimArea}
					disabled={loading}
					defaultValue={"All"}
					value={area}
					style={{
						width: "200px",
					}}
				/>
				<Search
					placeholder="Search"
					enterButton
					onSearch={onSearch}
					allowClear
					style={{
						width: 400,
					}}
					size="large"
					loading={loading}
					disabled={loading}
				/>
			</Flex>
		);
	};

	const onSuccessComment = () => {
		setModalCommentsOpen(false);
		currentClaimId.current = null;
	};

	const ModalComments = () => {
		return (
			<Modal
				title="Claim Comments"
				open={modalCommentsOpen}
				onCancel={() => setModalCommentsOpen(false)}
				cancelButtonProps={{ style: { display: "none" } }}
				okButtonProps={{ style: { display: "none" } }}
				width={1000}
			>
				<ClaimComment
					claimId={currentClaimId.current}
					onSuccess={onSuccessComment}
				/>
			</Modal>
		);
	};

	const ModalType = () => {
		return (
			<Modal
				title="Claim Type"
				open={modalTypeOpen}
				onCancel={() => setModalTypeOpen(false)}
				cancelButtonProps={{ style: { display: "none" } }}
				okButtonProps={{ style: { display: "none" } }}
				centered
				width={500}
				destroyOnClose={false}
			>
				<ClaimType setModalTypeOpen={setModalTypeOpen} />
			</Modal>
		);
	};

	return (
		<Layout title="Claim Dashboard" description="Dashboard">
			{contextHolder}
			<Flex vertical gap={"large"}>
				<RenderSearch />
				<ClaimInfoTop
					startDate={startDate}
					endDate={endDate}
					term={term}
					changed={changed}
				/>
				{loading ? <CustomSpinner /> : <RenderTable />}
				<ModalComments />
				<ModalType />
			</Flex>
		</Layout>
	);
};

export default ClaimDashboard;
